import {type RefObject, useEffect} from 'react'

interface UseCommentAnchorOptions {
  isAnchorable: boolean
  commentId?: string
  commentAnchor?: string
  containerRef: RefObject<HTMLElement>
}

/**
 * Hook that handles focusing a comment when the URL hash matches the comment anchor.
 * This allows direct linking to specific comments with proper focus management.
 *
 * It only handles focus for initial navigation to a comment. Subsequent changes to the hash
 * are handled by the `initialExpandedThreadId` logic.
 *
 * It also handles focus when a NEW comment is added.
 */
export function useCommentAnchor({
  isAnchorable,
  commentId,
  commentAnchor,
  containerRef,
}: UseCommentAnchorOptions): void {
  useEffect(() => {
    if (!isAnchorable) return

    if (commentId && commentAnchor && window.location.hash.split('#').pop() === commentAnchor) {
      containerRef.current?.focus()
    }
  }, [commentAnchor, commentId, isAnchorable, containerRef])
}
